import { useCallback, useEffect, useState } from "react";
import { Ticket, Plus, RefreshCw, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CardCreateDialog } from "@/components/admin/cards/CardCreateDialog";
import { CardDetailDialog } from "@/components/admin/cards/CardDetailDialog";
import { LegacyOrders } from "@/components/admin/cards/LegacyOrders";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const STATUS_STYLE = {
  unpaid: "bg-yellow-500/20 text-yellow-500",
  active: "bg-green-500/20 text-green-500",
  used_up: "bg-white/10 text-[#A1A1AA]",
  deactivated: "bg-red-500/20 text-red-400",
  expired: "bg-orange-500/20 text-orange-400",
};

export const cardApi = async (adminToken, path, method = "GET", body) => {
  const res = await fetch(`${API_URL}/api/cards/admin${path}`, {
    method, headers: { "Content-Type": "application/json", "X-Admin-Token": adminToken },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Aktion fehlgeschlagen");
  return data;
};

export default function TrainingskartenTab({ adminToken }) {
  const [cards, setCards] = useState([]);
  const [stats, setStats] = useState({});
  const [isPublic, setIsPublic] = useState(false);
  const [hideTest, setHideTest] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    try {
      const d = await cardApi(adminToken, `/list?include_test=${!hideTest}`);
      setCards(d.cards); setStats(d.stats); setIsPublic(d.public_enabled);
    } catch (e) { toast.error(e.message); }
  }, [adminToken, hideTest]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="cards-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-['Bebas_Neue'] text-3xl flex items-center gap-2"><Ticket className="w-6 h-6 text-[#c1ff72]" /> TRAININGSKARTEN</h2>
          <p className={`text-xs mt-1 ${isPublic ? "text-green-500" : "text-blue-400"}`} data-testid="cards-mode">
            {isPublic ? "LIVE: Kunden können Karten unter /buchen einlösen" : "TESTMODUS: Für Kunden ausgeblendet. Testen unter /buchen?kartentest=1 und /meine-karte?kartentest=1 (als Admin angemeldet)"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="btn-cards-refresh"><RefreshCw className="w-4 h-4" /></Button>
          <Button onClick={() => setShowCreate(true)} className="bg-[#c1ff72] text-[#0A0A0A] hover:bg-[#a8e65c]" data-testid="btn-card-create">
            <Plus className="w-4 h-4 mr-1" /> Karte erfassen
          </Button>
        </div>
      </div>

      <LegacyOrders adminToken={adminToken} onConverted={load} />

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[["active", "Aktiv"], ["unpaid", "Nicht bezahlt"], ["used_up", "Aufgebraucht"], ["deactivated", "Deaktiviert"], ["expired", "Abgelaufen"]].map(([k, l]) => (
          <div key={k} className="bg-[#1A1A1A] rounded-lg p-3 border border-white/10" data-testid={`cards-stat-${k}`}>
            <p className="text-xs text-[#71717A]">{l}</p><p className="font-['Bebas_Neue'] text-2xl">{stats[k] || 0}</p>
          </div>
        ))}
      </div>

      <label className="flex items-center gap-2 text-sm text-[#A1A1AA]">
        <input type="checkbox" checked={hideTest} onChange={(e) => setHideTest(e.target.checked)} data-testid="toggle-hide-test" /> Testdaten (@example.com) ausblenden
      </label>

      <div className="bg-[#1A1A1A] rounded-lg border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-white/10 text-[#71717A] text-left">
            <th className="p-3">Karte</th><th className="p-3">Kunde</th><th className="p-3">Training</th>
            <th className="p-3">Guthaben</th><th className="p-3">Zahlung</th><th className="p-3">Status</th><th className="p-3" />
          </tr></thead>
          <tbody>
            {cards.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-[#71717A]">Noch keine Trainingskarten</td></tr>}
            {cards.map((c) => (
              <tr key={c.card_id} className="border-b border-white/5" data-testid={`card-row-${c.card_id}`}>
                <td className="p-3"><p className="font-mono text-[#c1ff72]">{c.card_id}</p><p className="text-xs text-[#71717A]">{c.package_name}{c.is_test && " · TEST"}</p></td>
                <td className="p-3"><p>{c.customer.name}</p><p className="text-xs text-[#71717A]">{c.customer.email}</p></td>
                <td className="p-3">{c.training_name}<p className="text-xs text-[#71717A]">60 Min.</p></td>
                <td className="p-3" data-testid={`card-balance-${c.card_id}`}>{c.credits_remaining} / {c.credits_total}{c.credits_reserved > 0 && <p className="text-xs text-[#f59e0b]">{c.credits_reserved} reserviert</p>}</td>
                <td className="p-3">{c.payment_status === "paid" ? "Bezahlt" : "Offen"}<p className="text-xs text-[#71717A]">{c.price} €</p></td>
                <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLE[c.status]}`}>{c.status_label}</span></td>
                <td className="p-3 text-right"><Button size="sm" variant="ghost" onClick={() => setDetailId(c.card_id)} data-testid={`btn-card-detail-${c.card_id}`}><Eye className="w-4 h-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CardCreateDialog open={showCreate} onClose={() => setShowCreate(false)} adminToken={adminToken} onCreated={load} />
      {detailId && <CardDetailDialog cardId={detailId} adminToken={adminToken} onClose={() => setDetailId(null)} onChanged={load} />}
    </div>
  );
}
